import { useEffect, useState, useCallback } from "react";
import {
  Drawer,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
  Toolbar,
  Typography,
  Avatar,
  Stack,
  CircularProgress,
  useTheme,
  alpha,
} from "@mui/material";
import { HelpOutline } from "@mui/icons-material";
import * as MuiIcons from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useThemeMode } from "../theme/ThemeProvider";
import { useFontSize } from "../hooks/useFontSize";

import { useSearch } from "../contexts/SearchContext";
import { useDrawerWidth } from "./MainLayout";
import SupportChat from "../components/SupportChat";

const minDrawerWidth = 240; // Largura mínima
const maxDrawerWidth = 500; // Largura máxima

export default function MenuLateral({ open = true, rotasModulos = [] }) {
    const [usuario, setUsuario] = useState(null);
    const [dataHoraAtual, setDataHoraAtual] = useState(new Date());
    const [isResizing, setIsResizing] = useState(false);
    const { drawerWidth, setDrawerWidth } = useDrawerWidth();
  const navigate = useNavigate();
  const { mode } = useThemeMode();
  const theme = useTheme();
  const { searchTerm, clearSearch } = useSearch();
  const { getCurrentFontSize } = useFontSize();

  useEffect(() => {
    const stored = localStorage.getItem("usuario");
    if (stored) {
      try {
        const user = JSON.parse(stored);
        
        // Verifica se os dados necessários existem
        if (!user.st_nome || !user.st_email) {
          // Dados incompletos
        }
        
        setUsuario({
          nome_completo: user.st_nome,
          email: user.st_email
        });

        // Não carrega módulos aqui - será feito pelo MainLayout
        // carregarModulosDisponiveis(user)
        //   .then(rotas => {
        //     console.log('Rotas carregadas no MenuLateral:', rotas);
        //     setRotas(rotas);
        //   })
        //   .catch(err => {
        //     console.error('Erro ao carregar rotas no MenuLateral:', err);
        //     setRotas([]);
        //   });
      } catch (error) {
        setUsuario(null);
      }
    } else {
      setUsuario(null);
    }
  }, []);

  // Atualiza data e hora a cada segundo
  useEffect(() => {
    const interval = setInterval(() => {
      setDataHoraAtual(new Date());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Salva largura no localStorage quando alterada
  useEffect(() => {
    localStorage.setItem('drawerWidth', drawerWidth.toString());
  }, [drawerWidth]);

  // Funções de redimensionamento
  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsResizing(true);
  };

  const handleMouseMove = useCallback((e) => {
    if (!isResizing) return;
    
    const newWidth = e.clientX;
    if (newWidth >= minDrawerWidth && newWidth <= maxDrawerWidth) {
      setDrawerWidth(newWidth);
    }
  }, [isResizing, setDrawerWidth]);

  const handleMouseUp = () => {
    setIsResizing(false);
  };

  // Event listeners para redimensionamento
  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, handleMouseMove, handleMouseUp]);

  // Função para formatar data e hora
  const formatarDataHora = (data) => {
    const opcoesData = {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'America/Sao_Paulo'
    };
    
    const opcoesHora = {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZone: 'America/Sao_Paulo'
    };

    const dataFormatada = data.toLocaleDateString('pt-BR', opcoesData);
    const horaFormatada = data.toLocaleTimeString('pt-BR', opcoesHora);
    
    return { data: dataFormatada, hora: horaFormatada };
  };

  const getMuiIconByName = (nomeIcone) => {
    if (!nomeIcone) return <HelpOutline sx={{ color: "#FFC107" }} />;

    // Mapeamento especial para ícones que não seguem o padrão
    const mapeamentoEspecial = {
      'restore': 'Restore',
      'dashboard': 'Dashboard',
      'users': 'People',
      'user': 'Person',
      'company': 'Business',
      'service': 'Build',
      'schedule': 'Schedule',
      'finance': 'AttachMoney',
      'report': 'Assessment',
      'settings': 'Settings'
    };

    // Verifica se há um mapeamento especial
    if (mapeamentoEspecial[nomeIcone]) {
      const nomeMapeado = mapeamentoEspecial[nomeIcone];
      if (Object.prototype.hasOwnProperty.call(MuiIcons, nomeMapeado)) {
        const Icon = MuiIcons[nomeMapeado];
        return <Icon sx={{ color: "#FFC107" }} />;
      }
    }

    const variantes = [
      nomeIcone,
      `${nomeIcone}Outlined`,
      `${nomeIcone}Rounded`,
      `${nomeIcone}TwoTone`,
      `${nomeIcone}Sharp`,
    ];

    for (let nome of variantes) {
      if (Object.prototype.hasOwnProperty.call(MuiIcons, nome)) {
        const Icon = MuiIcons[nome];
        return <Icon sx={{ color: "#FFC107" }} />;
      }
    }

    return <HelpOutline sx={{ color: "#FFC107" }} />;
  };

  // Função para filtrar as rotas
  const rotasFiltradas = rotasModulos.filter(rota => {
    const searchLower = searchTerm.toLowerCase();
    return (
      rota.nome.toLowerCase().includes(searchLower) ||
      rota.path.toLowerCase().includes(searchLower) ||
      (rota.icone && rota.icone.toLowerCase().includes(searchLower))
    );
  });

  if (!usuario) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Drawer
        variant="persistent"
        sx={{
          width: open ? drawerWidth : 0,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: {
            width: drawerWidth,
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            height: '100vh',
            background: mode === "dark"
              ? "linear-gradient(180deg, #1a1a1a 0%, #121212 100%)"
              : "#e3f2fd",
            borderRight: "none",
            boxShadow: "0px 0px 20px rgba(0, 0, 0, 0.1)",
            transition: isResizing ? 'none' : theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
            overflowX: 'hidden',
            ...(open ? {} : { width: 0 }),
            // Garantindo que não há margens ou padding extras
            margin: 0,
            padding: 0,
            position: 'relative',
          },
        }}
        open={open}
      >
      {/* Topo */}
      <Box>
        <Toolbar sx={{ minHeight: "80px", p: 2 }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Avatar sx={{ bgcolor: "primary.main", width: 44, height: 44 }}>
              {usuario?.nome_completo?.[0]?.toUpperCase() || "U"}
            </Avatar>
            <Box>
              <Typography variant="subtitle1" fontWeight="bold" noWrap sx={{ lineHeight: 1.2 }}>
                {usuario?.nome_completo}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap sx={{ lineHeight: 1.2 }}>
                {usuario?.email}
              </Typography>
            </Box>
          </Stack>
        </Toolbar>

        <Divider sx={{ borderColor: mode === "dark" ? "rgba(255, 255, 255, 0.12)" : "rgba(0, 0, 0, 0.12)" }} />
      </Box>

      {/* Lista de Módulos com Scroll */}
      <Box sx={{ 
        flex: 1,
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        justifyContent: 'flex-start',
        p: 2,
        gap: '8px', // Espaçamento uniforme de 8px entre todos os itens
        '& > *': {
          width: '100%',
          flexShrink: 0, // Impede que os itens sejam comprimidos
          flexGrow: 0 // Impede que os itens se expandam
        }
      }}>
        {/* Indicador de resultados da pesquisa */}
        {searchTerm && (
          <Box sx={{ 
            px: 2, 
            py: 1, 
            height: '48px',
            minHeight: '48px',
            maxHeight: '48px',
            display: 'flex',
            alignItems: 'center',
            backgroundColor: mode === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.03)",
            borderRadius: 1
          }}>
            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
              {rotasFiltradas.length} módulo{rotasFiltradas.length !== 1 ? 's' : ''} encontrado{rotasFiltradas.length !== 1 ? 's' : ''}
            </Typography>
          </Box>
        )}

        {/* Módulos dinâmicos */}
        {rotasFiltradas.length > 0 ? (
          rotasFiltradas.map((rota) => (
            <ListItemButton
              key={`rota-${rota.path}`}
              onClick={() => {
                navigate(rota.path);
                // Limpa a pesquisa após navegar
                if (searchTerm) {
                  setTimeout(() => clearSearch(), 100);
                }
              }}
              sx={{
                borderRadius: 0,
                mx: 0,
                height: '48px',
                minHeight: '48px',
                maxHeight: '48px',
                transition: "all 0.3s ease",
                "&:hover": {
                  backgroundColor: mode === "dark"
                    ? "rgba(255, 255, 255, 0.08)"
                    : "rgba(0, 0, 0, 0.05)",
                  transform: "scale(1.02)",
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                {getMuiIconByName(rota.icone)}
              </ListItemIcon>
              <ListItemText 
              primary={
                searchTerm ? (
                  <span>
                    {rota.nome.split(new RegExp(`(${searchTerm})`, 'gi')).map((part, index) => 
                      part.toLowerCase() === searchTerm.toLowerCase() ? (
                        <span key={index} style={{ 
                          backgroundColor: theme.palette.mode === 'dark' ? '#ffd54f' : '#fff3cd',
                          color: theme.palette.mode === 'dark' ? '#000' : '#856404',
                          fontWeight: 'bold',
                          padding: '0 2px',
                          borderRadius: '2px'
                        }}>
                          {part}
                        </span>
                      ) : part
                    )}
                  </span>
                ) : rota.nome
              }
              sx={{ 
                m: 0,
                '& .MuiTypography-root': {
                  fontSize: getCurrentFontSize(),
                  fontWeight: 500,
                  lineHeight: 1.2
                }
              }}
            />
            </ListItemButton>
          ))
        ) : searchTerm && (
          <Box sx={{ 
            p: 2, 
            textAlign: 'center',
            color: 'text.secondary'
          }}>
            <Typography variant="body2" sx={{ lineHeight: 1.2 }}>
              Nenhum módulo encontrado para "{searchTerm}"
            </Typography>
          </Box>
        )}
      </Box>

      {/* Rodapé - Data e Hora */}
      <Box sx={{ 
        p: 2, 
        borderTop: 1, 
        borderColor: mode === "dark" ? "rgba(255, 255, 255, 0.12)" : "rgba(0, 0, 0, 0.12)",
        backgroundColor: mode === "dark" ? "rgba(255, 255, 255, 0.02)" : "rgba(0, 0, 0, 0.02)"
      }}>
        <Stack spacing={1} alignItems="center">
          <Typography 
            variant="body2" 
            sx={{ 
              color: mode === "dark" ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.7)",
              fontSize: '0.875rem',
              fontWeight: 500,
              textAlign: 'center',
              lineHeight: 1.2
            }}
          >
            {formatarDataHora(dataHoraAtual).data}
          </Typography>
          <Typography 
            variant="h6" 
            sx={{ 
              color: mode === "dark" ? "#FFC107" : "#1976d2",
              fontSize: '1.25rem',
              fontWeight: 600,
              textAlign: 'center',
              fontFamily: 'monospace',
              lineHeight: 1.2
            }}
          >
            {formatarDataHora(dataHoraAtual).hora}
          </Typography>
          <SupportChat />
        </Stack>
      </Box>
      </Drawer>
      
      {/* Handle de redimensionamento */}
      {open && (
        <Box
          onMouseDown={handleMouseDown}
          sx={{
            position: 'fixed',
            top: 0,
            left: drawerWidth - 2,
            width: 4,
            height: '100vh',
            cursor: 'col-resize',
            backgroundColor: 'transparent',
            zIndex: theme.zIndex.drawer + 2,
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.3),
            },
            '&:active': {
              backgroundColor: alpha(theme.palette.primary.main, 0.5),
            },
            transition: 'background-color 0.2s ease',
          }}
        />
      )}
    </>
  );
}
